import * as React from 'react'
import { Button, Table, Empty, InputNumber } from 'antd'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { FormattedMessage } from 'react-intl'
import SelectItem from '../../SelectItem'
import { OptionalAmount } from '../../Amount'
import { array, option } from 'fp-ts'
import { pipe } from 'fp-ts/lib/function'
import { InvoiceItem } from '../../../types'

type Props = {
  onChange?: (_: InvoiceItem[]) => void
  value?: InvoiceItem[]
  unitID?: string
}

const setAtIndex = <T,>(xs: T[] | undefined, i: number, x: T): T[] =>
  pipe(
    xs ?? [],
    array.modifyAt(i, () => x),
    option.getOrElse(() => [x])
  )

const ItemsForm = ({ value, onChange, unitID }: Props) => (
  <>
    <Table<InvoiceItem>
      dataSource={value ?? []}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<FormattedMessage id="noItems" />}
          />
        ),
      }}
      columns={[
        {
          dataIndex: 'name',
          title: <FormattedMessage id="name" />,
          width: '199px',
          render: (_, v, i) => (
            <SelectItem
              value={v.item}
              onChange={item =>
                onChange?.(setAtIndex(value, i, { item, quantity: v.quantity }))
              }
              selected={value?.map(i => i.item.id)}
            />
          ),
        },
        {
          dataIndex: 'price',
          title: <FormattedMessage id="price" />,
          render: (_, i) => (
            <OptionalAmount unit={unitID} amount={i.item.price} />
          ),
        },
        {
          dataIndex: 'quantity',
          title: <FormattedMessage id="quantity" />,
          render: (_, item, i) => (
            <InputNumber
              defaultValue={item.quantity}
              onChange={quantity =>
                onChange?.(setAtIndex(value, i, { ...item, quantity }))
              }
            />
          ),
        },
        {
          dataIndex: 'total',
          title: <FormattedMessage id="total" />,
          render: (_, i) => (
            <OptionalAmount
              unit={unitID}
              amount={i.item.price}
              map={x => x * i.quantity}
            />
          ),
        },
        {
          dataIndex: 'actions',
          render: (_, i) => (
            <Button
              icon={<DeleteOutlined />}
              onClick={() => {
                onChange?.(value?.filter(x => x.item.id !== i.item.id) ?? [])
              }}
            />
          ),
        },
      ]}
      pagination={false}
      size="small"
      bordered={false}
    />
    <Button
      type="dashed"
      onClick={() => {
        onChange?.([
          ...(value ?? []),
          {
            item: {
              id: 0,
              name: '',
              price: { '': 0 },
              description: '',
            },
            quantity: 0,
          },
        ])
      }}
      block
      icon={<PlusOutlined />}
    >
      <FormattedMessage id="addItem" />
    </Button>
  </>
)

export default ItemsForm
